import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@utils/cn';

/**
 * Skeleton variants using class-variance-authority
 */
const skeletonVariants = cva('animate-pulse rounded-md bg-muted', {
  variants: {
    variant: {
      default: 'bg-muted',
      light: 'bg-muted/50',
      shimmer:
        'relative overflow-hidden bg-muted before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_2s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface SkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {
  /**
   * Width of the skeleton (CSS value)
   */
  width?: string | number;
  /**
   * Height of the skeleton (CSS value)
   */
  height?: string | number;
  /**
   * Show as a circle (for avatars)
   */
  circle?: boolean;
}

/**
 * Skeleton Component
 *
 * A loading placeholder component that mimics the shape of content being loaded.
 *
 * @example
 * // Basic skeleton
 * <Skeleton className="h-4 w-full" />
 *
 * @example
 * // Skeleton with specific dimensions
 * <Skeleton width={200} height={20} />
 *
 * @example
 * // Circle skeleton (for avatars)
 * <Skeleton circle width={40} height={40} />
 *
 * @example
 * // Shimmer effect
 * <Skeleton variant="shimmer" className="h-20 w-full" />
 */
const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    { className, variant, width, height, circle, style, ...props },
    ref,
  ) => {
    const inlineStyle: React.CSSProperties = {
      ...style,
      ...(width && { width: typeof width === 'number' ? `${width}px` : width }),
      ...(height && { height: typeof height === 'number' ? `${height}px` : height }),
    };

    return (
      <div
        ref={ref}
        className={cn(
          skeletonVariants({ variant }),
          circle && 'rounded-full',
          className,
        )}
        style={inlineStyle}
        aria-busy="true"
        aria-live="polite"
        {...props}
      />
    );
  },
);
Skeleton.displayName = 'Skeleton';

/**
 * Skeleton Text Component
 *
 * Pre-configured skeleton for text lines
 */
export interface SkeletonTextProps extends Omit<SkeletonProps, 'circle'> {
  /**
   * Number of lines
   */
  lines?: number;
  /**
   * Width of the last line (as percentage of full width)
   */
  lastLineWidth?: number;
}

/**
 * SkeletonText Component
 *
 * Multiple skeleton lines for text content.
 *
 * @example
 * <SkeletonText lines={3} />
 * <SkeletonText lines={2} lastLineWidth={60} />
 */
const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 1,
  lastLineWidth = 100,
  className,
  ...props
}) => {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          className={cn('h-4', className)}
          style={
            index === lines - 1
              ? { width: `${lastLineWidth}%` }
              : undefined
          }
          {...props}
        />
      ))}
    </div>
  );
};
SkeletonText.displayName = 'SkeletonText';

/**
 * Skeleton Avatar Component
 */
export interface SkeletonAvatarProps extends Omit<SkeletonProps, 'circle'> {
  size?: number;
}

/**
 * SkeletonAvatar Component
 *
 * Circle skeleton for avatar placeholders.
 *
 * @example
 * <SkeletonAvatar />
 * <SkeletonAvatar size={64} />
 */
const SkeletonAvatar: React.FC<SkeletonAvatarProps> = ({
  size = 40,
  ...props
}) => {
  return <Skeleton circle width={size} height={size} {...props} />;
};
SkeletonAvatar.displayName = 'SkeletonAvatar';

/**
 * Skeleton Card Component
 *
 * Pre-configured skeleton for card layouts
 */
export interface SkeletonCardProps {
  /**
   * Show avatar in the card
   */
  showAvatar?: boolean;
  /**
   * Number of text lines
   */
  lines?: number;
  /**
   * Wrapper className
   */
  className?: string;
}

/**
 * SkeletonCard Component
 *
 * A complete skeleton card with avatar and text lines.
 *
 * @example
 * <SkeletonCard showAvatar lines={3} />
 */
const SkeletonCard: React.FC<SkeletonCardProps> = ({
  showAvatar = false,
  lines = 3,
  className,
}) => {
  return (
    <div className={cn('rounded-lg border bg-card p-6', className)}>
      <div className="flex items-center gap-4">
        {showAvatar && <SkeletonAvatar size={48} />}
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
      {lines > 0 && (
        <div className="mt-4">
          <SkeletonText lines={lines} lastLineWidth={70} />
        </div>
      )}
    </div>
  );
};
SkeletonCard.displayName = 'SkeletonCard';

/**
 * Skeleton Table Component
 *
 * Pre-configured skeleton for table layouts
 */
export interface SkeletonTableProps {
  /**
   * Number of rows
   */
  rows?: number;
  /**
   * Number of columns
   */
  columns?: number;
  /**
   * Show table header
   */
  showHeader?: boolean;
  /**
   * Wrapper className
   */
  className?: string;
}

/**
 * SkeletonTable Component
 *
 * A skeleton placeholder for tables.
 *
 * @example
 * <SkeletonTable rows={5} columns={4} showHeader />
 */
const SkeletonTable: React.FC<SkeletonTableProps> = ({
  rows = 5,
  columns = 4,
  showHeader = true,
  className,
}) => {
  return (
    <div className={cn('space-y-3', className)}>
      {showHeader && (
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={index} className="h-4" />
          ))}
        </div>
      )}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} className="h-8" />
          ))}
        </div>
      ))}
    </div>
  );
};
SkeletonTable.displayName = 'SkeletonTable';

/**
 * Skeleton Form Component
 *
 * Pre-configured skeleton for form layouts
 */
export interface SkeletonFormProps {
  /**
   * Number of form fields
   */
  fields?: number;
  /**
   * Show action buttons
   */
  showActions?: boolean;
  /**
   * Wrapper className
   */
  className?: string;
}

/**
 * SkeletonForm Component
 *
 * A skeleton placeholder for forms.
 *
 * @example
 * <SkeletonForm fields={5} showActions />
 */
const SkeletonForm: React.FC<SkeletonFormProps> = ({
  fields = 3,
  showActions = true,
  className,
}) => {
  return (
    <div className={cn('space-y-6', className)}>
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
      {showActions && (
        <div className="flex gap-2">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-24" />
        </div>
      )}
    </div>
  );
};
SkeletonForm.displayName = 'SkeletonForm';

/**
 * Skeleton List Component
 *
 * Pre-configured skeleton for list layouts
 */
export interface SkeletonListProps {
  /**
   * Number of list items
   */
  items?: number;
  /**
   * Show avatar in each item
   */
  showAvatar?: boolean;
  /**
   * Wrapper className
   */
  className?: string;
}

/**
 * SkeletonList Component
 *
 * A skeleton placeholder for lists.
 *
 * @example
 * <SkeletonList items={5} showAvatar />
 */
const SkeletonList: React.FC<SkeletonListProps> = ({
  items = 3,
  showAvatar = false,
  className,
}) => {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="flex items-center gap-3">
          {showAvatar && <SkeletonAvatar size={40} />}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
};
SkeletonList.displayName = 'SkeletonList';

export {
  Skeleton,
  SkeletonText,
  SkeletonAvatar,
  SkeletonCard,
  SkeletonTable,
  SkeletonForm,
  SkeletonList,
  skeletonVariants,
};
